import { useTranslation } from "react-i18next";
import { cmsText, mediaUrl } from "@/lib/cms";
import { Cta, Reveal } from "@/components/blocks/shared";

/**
 * Uitlegvideo-blok: HTML5-video met poster (controls, playsinline) naast titel,
 * subtitel, knoppen en een accent-pill. Video's worden geserveerd via /api/media
 * met byte-range (doorspoelen + iPhone).
 */
export const VideoBlock = ({ block }) => {
  const { i18n } = useTranslation();
  const T = (f) => cmsText(f, i18n.language);
  const video = mediaUrl(block.video);
  const poster = mediaUrl(block.poster);
  const pill = T(block.pill);

  return (
    <section className="relative overflow-hidden border-b bg-background" data-testid="block-video">
      <div className="absolute inset-0 hero-noise" aria-hidden="true" />
      <div className="absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8 lg:py-24">
        <Reveal className="lg:col-span-5">
          {T(block.eyebrow) && <p className="eyebrow">{T(block.eyebrow)}</p>}
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
            {T(block.titel)}
          </h1>
          {T(block.subtitel) && (
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg">
              {T(block.subtitel)}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Cta label={T(block.cta_label)} link={block.cta_link} testId="block-video-cta" arrow />
            <Cta label={T(block.cta2_label)} link={block.cta2_link} variant="outline" testId="block-video-cta2" />
          </div>
          {pill && (
            <span
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[hsl(43_92%_62%)] px-4 py-2 font-display text-sm font-semibold text-ink shadow-sm"
              data-testid="block-video-pill"
            >
              {pill}
            </span>
          )}
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="overflow-hidden rounded-3xl border bg-ink shadow-[0_40px_80px_-40px_hsl(30_20%_20%/0.6)]">
            {video ? (
              <video
                controls
                playsInline
                preload="metadata"
                poster={poster || undefined}
                className="aspect-video w-full bg-ink"
                data-testid="block-video-player"
              >
                <source src={video} type="video/mp4" />
              </video>
            ) : poster ? (
              <img src={poster} alt={T(block.titel)} className="aspect-video w-full object-cover" data-testid="block-video-poster" />
            ) : (
              <div className="flex aspect-video w-full items-center justify-center text-sm text-ink-foreground/60">
                Nog geen video ingesteld
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
